import { useState, useEffect, useCallback } from 'react';
import { getSitemap, getPageContent, getAudits, updateProject } from '../api';
import { useToast } from '../ToastContext';
import { normalizeReferencePageUrl, normalizePageUrl } from '../utils/referencePages';

export { normalizeReferencePageUrl };

export default function useReferencePages(project, iaData) {
    const slug = project?.slug;
    const { showToast } = useToast();

    const [sitemapUrls, setSitemapUrls] = useState([]);
    const [selectedReferencePages, setSelectedReferencePages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);
    const [modified, setModified] = useState(false);
    const [data, setData] = useState([]);
    const [activeTab, setActiveTab] = useState('selection');



    // Load sitemap
    useEffect(() => {
        if (!slug) return;
        const fetchSitemap = async () => {
            try {
                const res = await getSitemap(slug);
                setSitemapUrls(res.urls || []);
            } catch {
                // Sitemap unavailable
            }
        };
        fetchSitemap();
    }, [slug]);

    // Load existing selection — ignore entries older than 7 days
    useEffect(() => {
        if (project?.reference_pages) {
            const raw = localStorage.getItem(`reference_pages_${slug}`);
            if (raw) {
                try {
                    const { pages, savedAt } = JSON.parse(raw);
                    const age = Date.now() - (savedAt || 0);
                    if (Array.isArray(pages) && age < 7 * 24 * 60 * 60 * 1000) {
                        setSelectedReferencePages(pages);
                        setSaved(false);
                    } else {
                        localStorage.removeItem(`reference_pages_${slug}`);
                        setSelectedReferencePages(project.reference_pages);
                        setSaved(true);
                    }
                } catch {
                    localStorage.removeItem(`reference_pages_${slug}`);
                    setSelectedReferencePages(project.reference_pages);
                    setSaved(true);
                }
            } else {
                setSelectedReferencePages(project.reference_pages);
                setSaved(true);
            }
        }
    }, [project, slug]);

    // Save selection to local storage with timestamp (used for TTL check on load)
    useEffect(() => {
        if (slug && selectedReferencePages.length > 0) {
            localStorage.setItem(`reference_pages_${slug}`, JSON.stringify({ pages: selectedReferencePages, savedAt: Date.now() }));
        }
    }, [selectedReferencePages, slug]);

    const fetchData = useCallback(async (pages) => {
        if (!pages || pages.length === 0) {
            setData([]);
            return;
        }

        setLoading(true);

        const fetchSinglePage = async (url) => {
            try {
                const pageContent = await getPageContent(slug, url);
                return {
                    url: pageContent.url || url,
                    title: pageContent.title || '',
                    meta_description: pageContent.meta_description || '',
                    h1: pageContent.h1 || '',
                    h2: pageContent.h2 || [],
                    h3: pageContent.h3 || [],
                    content: pageContent.content || '',
                    content_preview: pageContent.content_preview || '',
                    nb_images: pageContent.nb_images || 0,
                    alt_texts: pageContent.alt_texts || [],
                    paragraphs: pageContent.paragraphs || []
                };
            } catch {
                try {
                    const audits = await getAudits(slug);
                    const norm = normalizePageUrl(url);
                    const audit = audits.find(
                        (a) => a.url === url || normalizePageUrl(a.url) === norm,
                    );
                    return {
                        url,
                        title: audit?.title || '',
                        meta_description: audit?.meta_description || '',
                        h1: audit?.h1 || '',
                        h2: [], h3: [], content: '', content_preview: '',
                        nb_images: audit?.nb_images || 0,
                        alt_texts: [], paragraphs: []
                    };
                } catch {
                    return { url, title: '', meta_description: '', h1: '', nb_images: 0 };
                }
            }
        };

        const results = await Promise.allSettled(pages.map(fetchSinglePage));
        const pagesData = results
            .filter(r => r.status === 'fulfilled')
            .map(r => r.value);

        setData(pagesData);
        setLoading(false);
    }, [slug]);

    // Trigger data fetch when selection changes or tab changes
    useEffect(() => {
        if (activeTab === 'utilisees' && selectedReferencePages.length > 0) {
            if (iaData?.reference_pages_data && iaData.reference_pages_data.length > 0) {
                setData(iaData.reference_pages_data);
            } else {
                fetchData(selectedReferencePages);
            }
        }
    }, [activeTab, selectedReferencePages, iaData, fetchData]);

    // Update data from iaData if available
    useEffect(() => {
        if (iaData?.reference_pages_data && iaData.reference_pages_data.length > 0) {
            setData(iaData.reference_pages_data);
        }
    }, [iaData]);

    // Toggle selection
    const togglePage = (url) => {
        setSelectedReferencePages(prev => {
            const newSelection = prev.includes(url)
                ? prev.filter(u => u !== url)
                : prev.length < 10
                    ? [...prev, url]
                    : prev;

            if (saved) {
                const original = project?.reference_pages || [];
                const isModified = JSON.stringify(newSelection.sort()) !== JSON.stringify(original.sort());
                setModified(isModified);
            }
            return newSelection;
        });
    };

    // Save to backend
    const saveReferencePages = async () => {
        if (selectedReferencePages.length === 0 || selectedReferencePages.length > 10) {
            showToast('Sélectionnez entre 1 et 10 pages.', 'error');
            return;
        }
        setLoading(true);
        try {
            const normalized = selectedReferencePages
                .map((u) => normalizeReferencePageUrl(u, project?.url) || u)
                .filter(Boolean);
            await updateProject(slug, { reference_pages: normalized });
            setSelectedReferencePages(normalized);
            setSaved(true);
            setModified(false);
            localStorage.removeItem(`reference_pages_${slug}`);
            showToast('Pages de référence enregistrées !', 'success');
        } catch (e) {
            showToast(e.message || 'Erreur sauvegarde', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getSortedUrls = () => {
        const selected = [...selectedReferencePages];
        const unselected = sitemapUrls.filter(url => !selectedReferencePages.includes(url));
        return [...selected, ...unselected];
    };

    const addCustomUrl = (rawUrl) => {
        const url = normalizeReferencePageUrl(rawUrl, project?.url);
        if (!url) {
            showToast('URL invalide. Saisissez une adresse complète (https://…) ou un chemin relatif (/page).', 'error');
            return false;
        }
        if (selectedReferencePages.includes(url)) {
            showToast('Cette page est déjà sélectionnée.', 'info');
            return false;
        }
        if (selectedReferencePages.length >= 10) {
            showToast('Maximum 10 pages de référence.', 'error');
            return false;
        }
        setSelectedReferencePages((prev) => {
            const next = [...prev, url];
            if (saved) {
                const original = project?.reference_pages || [];
                setModified(JSON.stringify(next.sort()) !== JSON.stringify([...original].sort()));
            }
            return next;
        });
        showToast('Page ajoutée à la sélection.', 'success');
        return true;
    };

    const removeCustomUrl = (url) => {
        setSelectedReferencePages((prev) => {
            const next = prev.filter((u) => u !== url);
            if (saved) {
                const original = project?.reference_pages || [];
                setModified(JSON.stringify(next.sort()) !== JSON.stringify([...original].sort()));
            }
            return next;
        });
    };

    return {
        sitemapUrls,
        selectedReferencePages,
        loading,
        saved,
        modified,
        data,
        activeTab,
        setActiveTab,
        togglePage,
        addCustomUrl,
        removeCustomUrl,
        saveReferencePages,
        getSortedUrls,
        fetchData,
    };
}
